import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, AlertCircle, CheckCircle, X } from 'lucide-react';

interface VerificationPendingBannerProps {
  email: string;
  onResendVerification: (email: string) => Promise<void>;
  onClose: () => void;
}

const VerificationPendingBanner: React.FC<VerificationPendingBannerProps> = ({ 
  email, 
  onResendVerification, 
  onClose 
}) => {
  const [isResending, setIsResending] = useState(false);
  const [resendStatus, setResendStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [resendMessage, setResendMessage] = useState('');

  const handleResendVerification = async () => {
    setIsResending(true);
    setResendStatus('idle');
    setResendMessage('');

    try {
      await onResendVerification(email);
      setResendStatus('success');
      setResendMessage('Verification email sent successfully!');
      
      // Reset status after 3 seconds
      setTimeout(() => {
        setResendStatus('idle');
        setResendMessage('');
      }, 3000);
    } catch (error) {
      setResendStatus('error');
      setResendMessage(error instanceof Error ? error.message : 'Failed to resend verification email');
    } finally {
      setIsResending(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg relative"
    >
      {/* Close Button */}
      <button
        onClick={onClose}
        className="absolute top-3 right-3 text-blue-400 hover:text-blue-600 transition-colors"
        aria-label="Close banner"
      >
        <X className="w-4 h-4" />
      </button>
      
      <div className="flex items-start space-x-3">
        <div className="flex-shrink-0">
          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
            <Mail className="w-5 h-5 text-blue-600" />
          </div>
        </div>
        
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-medium text-blue-800 mb-2">
            Email verification required
          </h3>
          
                      <div className="text-sm text-blue-700 space-y-2">
              <p>
                We've sent a verification email to <strong className="font-semibold">{email}</strong>.
              </p>
              <p>
                Please check your inbox and click the verification link to complete your waitlist registration.
              </p>
            </div>
          
          {/* Resend Status Messages */}
          <AnimatePresence mode="wait">
            {resendStatus === 'success' && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="mt-3 p-2 bg-green-100 border border-green-200 rounded-md"
              >
                <div className="flex items-center space-x-2 text-green-700">
                  <CheckCircle className="w-4 h-4" />
                  <span className="text-sm font-medium">{resendMessage}</span>
                </div>
              </motion.div>
            )}
            
            {resendStatus === 'error' && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="mt-3 p-2 bg-red-100 border border-red-200 rounded-md"
              >
                <div className="flex items-center space-x-2 text-red-700">
                  <AlertCircle className="w-4 h-4" />
                  <span className="text-sm font-medium">{resendMessage}</span>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
          
          {/* Action Buttons */}
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <button
              onClick={handleResendVerification}
              disabled={isResending}
              className={`
                inline-flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-colors
                ${isResending
                  ? 'bg-blue-100 text-blue-400 cursor-not-allowed'
                  : 'bg-blue-100 hover:bg-blue-200 text-blue-700'
                }
              `}
            >
              {isResending ? (
                <>
                  <div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin"></div>
                  <span>Sending...</span>
                </>
              ) : (
                <>
                  <Mail className="w-4 h-4" />
                  <span>Resend verification email</span>
                </>
              )}
            </button>
            
            <button
              onClick={onClose}
              className="text-blue-600 hover:text-blue-800 text-sm font-medium underline transition-colors"
            >
              I'll check my email later
            </button>
          </div>
          
          {/* Additional Help */}
          <div className="mt-3 text-xs text-blue-600">
            <p>💡 <strong>Tip:</strong> Check your spam folder if you don't see the email within a few minutes.</p>
            <p>⏰ <strong>Note:</strong> Verification links expire in 24 hours for security.</p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default VerificationPendingBanner;
